import { queryOptions, useQuery } from '@tanstack/react-query';

import { useLocale } from 'hooks/useLocale';
import { removeLocale } from 'queries/keys.utils';
import { QueryVariables, StaleTime } from 'queries/types';
import { TICKETS_PREFIX } from 'restful/tickets/constants';
import { Categories } from 'restful/tickets/types';
import { useTicketingEventId } from 'restful/tickets/utils';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type { TicketOfferCategory } from 'services/api/restful/generated/nofomoRestApi.schemas';
import { getTicketOffersByCode } from 'services/api/restful/generated/tickets/tickets';
import { SiteLocale } from 'types/api/dato.types';

const mergeTicketOffersByCode = (
  offers: Array<TicketOfferCategory> | null | undefined,
  locale: SiteLocale,
): Categories =>
  (offers ?? []).reduce<Categories>((acc, offer) => {
    const tickets = offer.tickets ?? [];
    const uniqueTickets = Array.from(
      new Map(tickets.map((ticket) => [ticket.id, ticket])).values(),
    );
    const name = offer.name?.[locale] ?? '';
    acc.push({
      id: offer.id,
      name,
      categoryWithSubcategoryName: name,
      tickets: uniqueTickets,
    });
    return acc;
  }, []);

const ticketOffersByCodeQuery = (variables: {
  code: string;
  eventId: string;
  timeslotId: string;
  locale: SiteLocale;
}) =>
  queryOptions<Categories, RestApiError>({
    queryKey: [
      TICKETS_PREFIX,
      'ticket-offers-by-code',
      removeLocale(variables),
    ],
    queryFn: async () => {
      const { data } = await restCall(() =>
        getTicketOffersByCode(variables.eventId, {
          code: variables.code,
          ...(variables.timeslotId
            ? { timeslotId: variables.timeslotId }
            : undefined),
        }),
      );

      return mergeTicketOffersByCode(data, variables.locale);
    },
  });

export const useTicketOffersByCode = (
  {
    code,
  }: Partial<Pick<QueryVariables<typeof ticketOffersByCodeQuery>, 'code'>>,
  options?: Partial<ReturnType<typeof ticketOffersByCodeQuery>>,
) => {
  const { locale } = useLocale();
  const { eventId, timeslotId } = useTicketingEventId();
  const query = useQuery({
    ...ticketOffersByCodeQuery({
      eventId,
      timeslotId,
      code: code ?? '',
      locale,
    }),
    enabled: !!code,
    staleTime: StaleTime.ONE_HOUR,
    ...options,
  });

  return {
    ticketOffers: query.data ?? [],
    isLoading: query.isLoading,
    status: query.status,
    refetch: query.refetch,
  };
};
